import React from 'react'
import { useGetSettingsQuery } from '../../../frontend/services/appSlice/appSlice';
import { SEPAgeHighlight, SEPAgeKnockout, SEPAgeValidation, SEPPhase } from '../../../shared/types/SEP';
import { SEPSearchRow } from '../../../shared/types/SEP';
import { Stack, useTheme } from '@mui/material';

// SepAge component displays the age of a SEP with color highlighting based on settings and phase
const SepAge = ({ sepObject, showPhaseText = false }: { sepObject: SEPSearchRow, showPhaseText: boolean }) => {


    // Get the current theme for color values
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';


    // Fetch SEP age highlight settings (yellow and red thresholds)
    const { data: sepSettings } = useGetSettingsQuery();
    const sepAge1 = sepSettings
        ?.filter((help: { name: string; value?: string }) => help.name === SEPAgeHighlight.yellow)
    const sepAge2 = sepSettings
        ?.filter((help: { name: string; value?: string }) => help.name === SEPAgeHighlight.red)

    // Determine if SEP is finished
    const sepFinish = sepObject?.phase === SEPPhase.complete;
    const today = new Date();

    // Determine the date to use for age calculation
    const sepCreatedDate: any = sepObject?.validationDate
        ? Date.parse(sepObject.validationDate)
        : sepObject?.phase === SEPPhase?.validation
            ? SEPPhase?.validation // If in validation phase, use a special marker
            : sepObject?.phase === SEPPhase?.knockout
            ? SEPPhase?.knockout // If in knockout phase, use a special marker
            : sepObject?.startedAt ? Date.parse(
                typeof sepObject?.startedAt === 'string'
                    ? sepObject.startedAt
                    : sepObject?.startedAt instanceof Date
                        ? sepObject.startedAt.toISOString()
                        : ''
            ): sepObject?.createdAt && Date.parse(sepObject?.createdAt);

    // Only create a date object if not in validation phase
    const dateObject =
        (sepCreatedDate !== SEPPhase?.validation && sepCreatedDate !== SEPPhase?.knockout) ? new Date(sepCreatedDate) : null;

    // Calculate SEP age in days, or show validation text/0 if not available
    let sepAge: any;
    if (dateObject) {
        let diffInMs: number;
        if (sepFinish && sepObject?.completedAt) {
            // If SEP is finished, use completedAt for age calculation
            diffInMs = Math.abs(
                new Date(sepObject.completedAt).getTime() - dateObject.getTime()
            );
        } else {
            // Otherwise, use today's date
            diffInMs = Math.abs(today.getTime() - dateObject.getTime());
        }
        // Convert milliseconds to days
        sepAge = Math.round(diffInMs / (1000 * 60 * 60 * 24));
    } else {
        // If no valid date, show validation text or 0
        sepAge = showPhaseText ? (sepObject?.phase === SEPPhase?.validation ? SEPAgeValidation : 
            sepObject?.phase === SEPPhase?.knockout ? SEPAgeKnockout : "NA") : "NA";
    }


    // Determine background color based on age thresholds
    const ageHighlight = sepAge2 && sepAge !== SEPAgeValidation && sepAge > parseInt(sepAge2[0]?.value)
        ? theme.palette.deepRed.solidRed // Red if above red threshold
        : sepAge1 && sepAge !== SEPAgeValidation && sepAge > parseInt(sepAge1[0]?.value)
            ? theme.palette.yellow.main // Yellow if above yellow threshold
            : '';


    // Set text color for accessibility based on background
    let textColor = isDarkMode ? theme.palette.white.main : theme.palette.content.default; // Default text color for light/dark mode
    if (sepObject?.phase === SEPPhase?.complete) {
        textColor = theme.palette.white.main; // white text on dark blue
    } else if (ageHighlight === theme.palette.deepRed.solidRed) {
        textColor = theme.palette.white.main; // white text on red
    } else if (ageHighlight === theme.palette.yellow.main) {
        textColor = theme.palette.content.default; // dark text on yellow
    }

    // Render the SEP age with dynamic background and text color
    return (
        <Stack
            style={{
                backgroundColor: sepObject?.phase === SEPPhase?.complete ? theme.palette.solidBlue.main : ageHighlight,
                color: textColor,
                padding: '3px 0px 3px 0',
                margin: '3px 3px 3px 0',
                borderRadius: '4px',
                minWidth: '40px',
                maxWidth: sepAge === SEPAgeValidation ? 'fit-content' : '40px',
                textAlign: 'center',
                fontSize: '14px'
            }}
        >
            {sepAge}
        </Stack>
    );
}

export default SepAge